import React from 'react'

interface State {
  error: Error | null
}

// Catches uncaught render-time errors so the WebView never shows a fully
// blank screen. The most common trigger today is a refresh in the Telegram
// menu that drops the `#tgWebAppData=` URL hash — without this boundary,
// the next throw from any SDK hook would unmount the whole tree.
export class RootErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RootErrorBoundary]', error, info)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #EEF2FF 0%, #EDF4FF 50%, #F0F4F8 100%)',
          fontFamily: 'Roboto, sans-serif',
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', maxWidth: 320 }}>
          <span style={{ fontSize: 48 }}>😔</span>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C2D41', margin: 0 }}>Что-то пошло не так</h2>
          <p style={{ fontSize: 15, color: '#6B7B8D', margin: 0, lineHeight: 1.5 }}>
            Откройте приложение заново — обновление страницы в меню Telegram теряет авторизационные данные.
          </p>
          {/* On dev/local, surface the real error so we don't misattribute a
              render crash (e.g. a broken Analytics computation) to "auth lost". */}
          {(import.meta.env.DEV || window.location.hostname.includes('-dev.')) && (
            <pre style={{
              maxWidth: '100%', textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontSize: 11, lineHeight: 1.4, color: '#B91C1C', background: 'rgba(185,28,28,0.06)',
              border: '1px solid rgba(185,28,28,0.18)', borderRadius: 10, padding: 10, margin: 0,
              maxHeight: 260, overflow: 'auto',
            }}>
              {this.state.error.message}
              {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
            </pre>
          )}
          <button
            onClick={() => {
              try {
                window.Telegram?.WebApp?.close?.()
              } catch {
                // ignore — fall through to reload below
              }
              window.location.reload()
            }}
            style={{
              width: '100%',
              height: 54,
              borderRadius: 18,
              border: 'none',
              background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
              color: 'white',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              marginTop: 8,
              boxShadow: '0 6px 24px rgba(108,92,231,0.35)',
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    )
  }
}
